import React, { type ReactNode } from 'react';
import type { Store } from 'redux';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { Provider as ReduxProvider } from '@atlassian/jira-react-redux/src/index.tsx';

type Props = {
	children: ReactNode;
	/**
	 * ID for error reporting.
	 * @see {JSErrorBoundary}
	 */
	id?: string;
	/**
	 * Team name for error reporting.
	 * @see {JSErrorBoundary}
	 */
	teamName?: string;
	/**
	 * Package name for error reporting.
	 * @see {JSErrorBoundary}
	 */
	packageName?: string;
	/**
	 * Fallback behaviour when error encountered. Default: 'flag'.
	 * @see {JSErrorBoundary}
	 */
	fallback?: 'flag' | 'page';
	/**
	 * Redux store to add a provider for. Default: none.
	 * @see {Store}
	 * @see {ReduxProvider}
	 */
	store?: Store<unknown>;
	/**
	 * Whether an error boundary should wrap the provided children. Default: true.
	 * @see {JSErrorBoundary}
	 * @deprecated do not use AppBase if you don't want an error boundary!
	 */
	hasErrorBoundary?: boolean;
};

/**
 * A boundary around an "app," where an app is a distinct piece of functionality, and could potentially be nested within another app.
 *
 * Wraps children in an error boundary (by default) and a redux store provider (if store is passed).
 */
function AppBaseFC({
	// FIXME: id should be required
	id = '@atlassian/jira-app-base::unknown-app',
	// FIXME: packageName should be required
	packageName = '@atlassian/jira-app-base',
	teamName,
	hasErrorBoundary = true,
	// JSErrorBoundary fallback default is 'page' but AppBase is used around any "app"
	fallback = 'flag',
	store,
	children,
}: Props) {
	let contents: ReactNode = children;

	if (store) {
		contents = <ReduxProvider store={store}>{contents}</ReduxProvider>;
	}

	if (hasErrorBoundary) {
		contents = (
			<JSErrorBoundary id={id} teamName={teamName} fallback={fallback} packageName={packageName}>
				{contents}
			</JSErrorBoundary>
		);
	}

	return contents;
}

export default AppBaseFC;
